<script setup>
// Settings → Notifications: push on this device and which events to send (docs/notifications/SPEC.md, decision 3).
import axios from 'axios';
import { computed, onMounted, reactive, ref } from 'vue';
import { extractApiError } from '../apiError';
import { t } from '../i18n';
import {
  currentSubscription, disablePush, enablePush, isPushSupported, needsHomeScreen, permission, sendTestPush,
  syncSubscription,
} from '../push';

// rows: a «Профиль» group on a phone (docs/design/SPEC.md, «Профиль»).
defineProps({
  rows: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['message', 'error']);

const EVENTS = ['payout_received', 'dividend_declared', 'record_date', 'achievement'];

const loaded = ref(false);
const busy = ref(false);
const configured = ref(false);
const vapidKey = ref('');
const subscription = ref(null);
const settings = reactive(Object.fromEntries(EVENTS.map((event) => [event, true])));
const savingEvent = ref('');

const supported = isPushSupported();
const homeScreen = needsHomeScreen();
const deviceOn = computed(() => subscription.value !== null);

// Why the device switch is off and cannot be turned on, or null.
const blocker = computed(() => {
  if (homeScreen) {
    return t('notifications.homeScreen');
  }
  if (!supported) {
    return t('notifications.unsupported');
  }
  if (!configured.value) {
    return t('notifications.notConfigured');
  }
  if (!deviceOn.value && permission() === 'denied') {
    return t('notifications.denied');
  }
  return null;
});

function apply(data) {
  configured.value = data.configured;
  vapidKey.value = data.vapid_public_key || '';
  Object.assign(settings, data.settings);
}

async function load() {
  try {
    const { data } = await axios.get('/user/notifications');
    apply(data);
    const existing = supported && permission() === 'granted' ? await currentSubscription() : null;
    if (existing) {
      apply(await syncSubscription(existing));
      subscription.value = existing;
    }
  } catch (e) {
    if (e?.response?.status !== 401 && e?.response?.status !== 423) {
      emit('error', extractApiError(e, t('settings.loadFailed')));
    }
  } finally {
    loaded.value = true;
  }
}

async function toggleDevice() {
  if (busy.value || (blocker.value && !deviceOn.value)) {
    return;
  }
  busy.value = true;
  try {
    if (deviceOn.value) {
      const data = await disablePush();
      if (data) {
        apply(data);
      }
      subscription.value = null;
      emit('message', t('notifications.off'));
    } else {
      const result = await enablePush(vapidKey.value);
      apply(result.data);
      subscription.value = result.subscription;
      emit('message', t('notifications.on'));
    }
  } catch (e) {
    if (e?.code === 'denied') {
      emit('error', t('notifications.denied'));
    } else {
      emit('error', extractApiError(e, t('notifications.failed')));
    }
  } finally {
    busy.value = false;
  }
}

async function toggleEvent(event) {
  if (savingEvent.value) {
    return;
  }
  const value = !settings[event];
  settings[event] = value;
  savingEvent.value = event;
  try {
    const { data } = await axios.patch('/user/notifications', { [event]: value });
    apply(data);
  } catch (e) {
    settings[event] = !value;
    emit('error', extractApiError(e, t('notifications.failed')));
  } finally {
    savingEvent.value = '';
  }
}

async function test() {
  if (!subscription.value || busy.value) {
    return;
  }
  busy.value = true;
  try {
    const data = await sendTestPush(subscription.value);
    emit('message', data.message);
  } catch (e) {
    emit('error', extractApiError(e, t('notifications.failed')));
  } finally {
    busy.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div v-if="rows">
    <h2 class="px-5 pb-1.5 pt-6 text-[0.8125rem] uppercase tracking-wide text-slate-500">
      {{ t('notifications.title') }}
    </h2>
    <div class="overflow-hidden rounded-3xl bg-slate-900">
      <div class="flex min-h-12 items-center justify-between gap-3 px-5 py-2">
        <span class="min-w-0">
          <span
            id="push-row-label"
            class="block text-[0.9375rem] text-slate-100"
          >{{ t('notifications.device') }}</span>
          <span
            v-if="loaded && blocker && !deviceOn"
            class="block text-[0.8125rem] leading-4 text-slate-500"
          >{{ blocker }}</span>
        </span>
        <button
          type="button"
          role="switch"
          :aria-checked="deviceOn"
          aria-labelledby="push-row-label"
          class="relative inline-flex h-[31px] w-[51px] shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
          :class="deviceOn ? 'bg-emerald-600' : 'bg-slate-700'"
          :disabled="busy || !loaded || (!!blocker && !deviceOn)"
          @click="toggleDevice"
        >
          <span
            class="inline-block size-[27px] rounded-full bg-white shadow transition-transform"
            :class="deviceOn ? 'translate-x-[22px]' : 'translate-x-0.5'"
          />
        </button>
      </div>
      <div
        v-for="event in EVENTS"
        :key="event"
        class="flex min-h-12 items-center justify-between gap-3 border-t border-slate-800 px-5 py-2"
      >
        <span
          :id="`push-event-${event}`"
          class="min-w-0 text-[0.9375rem] text-slate-100"
        >{{ t(`notifications.event.${event}`) }}</span>
        <button
          type="button"
          role="switch"
          :aria-checked="settings[event]"
          :aria-labelledby="`push-event-${event}`"
          class="relative inline-flex h-[31px] w-[51px] shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
          :class="settings[event] ? 'bg-emerald-600' : 'bg-slate-700'"
          :disabled="!loaded || savingEvent === event"
          @click="toggleEvent(event)"
        >
          <span
            class="inline-block size-[27px] rounded-full bg-white shadow transition-transform"
            :class="settings[event] ? 'translate-x-[22px]' : 'translate-x-0.5'"
          />
        </button>
      </div>
      <button
        v-if="deviceOn"
        type="button"
        class="flex min-h-12 w-full items-center border-t border-slate-800 px-5 text-left text-[0.9375rem] text-sky-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800 disabled:opacity-50"
        :disabled="busy"
        @click="test"
      >
        {{ t('notifications.test') }}
      </button>
    </div>
    <p class="px-5 pt-1.5 text-[0.8125rem] leading-4 text-slate-500">
      {{ t('notifications.hint') }}
    </p>
  </div>

  <section
    v-else
    class="rounded-lg border border-slate-700 bg-slate-900/40 p-5"
  >
    <h3 class="text-sm font-medium text-slate-300">
      {{ t('notifications.title') }}
    </h3>
    <p class="mt-1 text-xs text-slate-500">
      {{ t('notifications.hint') }}
    </p>

    <p
      v-if="!loaded"
      class="mt-4 text-sm text-slate-400"
    >
      {{ t('common.loading') }}
    </p>

    <template v-else>
      <div class="mt-5 flex items-center justify-between gap-4">
        <div class="min-w-0">
          <p
            id="push-label"
            class="text-sm font-medium text-slate-200"
          >
            {{ t('notifications.device') }}
          </p>
          <p
            v-if="blocker && !deviceOn"
            class="mt-0.5 text-xs text-slate-500"
          >
            {{ blocker }}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          :aria-checked="deviceOn"
          aria-labelledby="push-label"
          class="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
          :class="deviceOn ? 'bg-emerald-600' : 'bg-slate-700'"
          :disabled="busy || (!!blocker && !deviceOn)"
          @click="toggleDevice"
        >
          <span
            class="inline-block size-6 rounded-full bg-white shadow transition-transform"
            :class="deviceOn ? 'translate-x-[22px]' : 'translate-x-0.5'"
          />
        </button>
      </div>

      <div
        v-for="event in EVENTS"
        :key="event"
        class="mt-4 flex items-center justify-between gap-4"
      >
        <p
          :id="`push-desk-${event}`"
          class="text-sm text-slate-200"
        >
          {{ t(`notifications.event.${event}`) }}
        </p>
        <button
          type="button"
          role="switch"
          :aria-checked="settings[event]"
          :aria-labelledby="`push-desk-${event}`"
          class="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
          :class="settings[event] ? 'bg-emerald-600' : 'bg-slate-700'"
          :disabled="savingEvent === event"
          @click="toggleEvent(event)"
        >
          <span
            class="inline-block size-6 rounded-full bg-white shadow transition-transform"
            :class="settings[event] ? 'translate-x-[22px]' : 'translate-x-0.5'"
          />
        </button>
      </div>

      <button
        v-if="deviceOn"
        type="button"
        class="mt-5 rounded bg-slate-800 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50"
        :disabled="busy"
        @click="test"
      >
        {{ t('notifications.test') }}
      </button>
    </template>
  </section>
</template>
